/* ==========================================================================
   ENA — Mega nav

   Two behaviours live here.

   1. SCROLL STATE (.pen `GsbMC` "02 Home con NAVBAR AL SCROLL"). In the design
      the scrolled header is the BASE component with no overrides at all:
      1440x80, white, no radius, flush to the top, dark content. The floating
      state over the hero is the exception, not the rule.

      Watch out when reading that file: its `black` / `white` variables are
      inverted, so the base fill reads `$black` and resolves to #FFFFFF. Going
      by the variable name gives a black bar, the opposite of the design.

   2. ANNOUNCEMENT REUSE. The design draws the announcement inside the floating
      pill, but the store already has Impact's own announcement bar section with
      its messages, rotation and arrows. Rather than rebuild that as a second
      swiper with a second set of texts to keep in sync, this moves the existing
      section into a slot inside the pill on desktop, and puts it back where it
      came from on phones, where this nav hides and Impact's header takes over.
      One source of truth: the messages stay editable in the announcement bar
      section exactly as before.
   ========================================================================== */
(function () {
  var DESKTOP = '(min-width: 1000px)';

  function initCompact(nav) {
    if (!nav.hasAttribute('data-compact-on-scroll')) return;

    /* Switch once the bar has scrolled past half its own height, so the two
       states never overlap mid-transition. Measured from the element rather
       than hardcoded, because the announcement is optional and the adopted bar
       is not always the same height. */
    function threshold() {
      var shell = nav.querySelector('.ena-nav__shell');
      return (shell ? shell.offsetHeight : 120) / 2;
    }

    var compact = false;
    var ticking = false;

    function apply() {
      ticking = false;
      var should = window.scrollY > threshold();
      if (should === compact) return;
      compact = should;
      nav.classList.toggle('is-compact', compact);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();
  }

  function initAnnouncement(nav) {
    var slot = nav.querySelector('[data-ena-announce-slot]');
    if (!slot) return;

    var bar = document.querySelector('.shopify-section--announcement-bar');
    if (!bar) {
      /* No announcement bar in the header group: fall back to the section's own
         static text, which stays hidden while a real bar is available. */
      nav.classList.add('ena-nav--no-announce');
      return;
    }

    /* A comment node holds the bar's original position so it can go back to the
       exact same place, instead of being appended to the end of the group. */
    var anchor = document.createComment('ena-nav announcement home');
    if (bar.parentNode) bar.parentNode.insertBefore(anchor, bar);

    var mq = window.matchMedia(DESKTOP);
    var adopted = false;

    function sync() {
      if (mq.matches && !adopted) {
        slot.appendChild(bar);
        adopted = true;
        nav.classList.add('ena-nav--has-announce');
      } else if (!mq.matches && adopted) {
        if (anchor.parentNode) anchor.parentNode.insertBefore(bar, anchor);
        adopted = false;
        nav.classList.remove('ena-nav--has-announce');
      }
    }

    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
    sync();
  }

  /* -------------------------------------------------------------------------
     3. PANELS. A trigger opens its panel and closes any other; clicking it
        again, clicking outside, pressing Escape or scrolling into the compact
        state closes it. The chevron flips off aria-expanded (CSS). Inside an
        open panel, hovering a row marks it active, greys the rest and rewrites
        the right-hand title to that row's label, which is the spec's hover
        behaviour (section 3). The row's products are the next build; the hook
        for them is `data-ena-nav-track` on the right.
     ------------------------------------------------------------------------- */
  function initPanels(nav) {
    var triggers = Array.prototype.slice.call(nav.querySelectorAll('[data-ena-nav-trigger]'));
    if (!triggers.length) return;

    var openId = null;
    var openedAtY = 0;
    /* True while the cursor is over a trigger or an open panel's white box.
       Scrolling with the cursor inside (wheeling over the cards, scrolling the
       category list) must not close the menu (22/09, Luchi). */
    var pointerInside = false;

    function panelFor(id) {
      return nav.querySelector('[data-ena-nav-panel="' + id + '"]');
    }

    /* Every row has its own server-rendered track, hidden until hovered. The
       default track is what shows when no row is active. Only one is visible
       at a time, so the right column never stacks. */
    function showTrack(panel, id) {
      var any = false;
      var shown = null;
      panel.querySelectorAll('[data-ena-nav-track]').forEach(function (t) {
        var on = t.getAttribute('data-ena-nav-track') === id;
        t.hidden = !on;
        if (on) { any = true; shown = t; }
      });
      if (!any && id !== 'default') return showTrack(panel, 'default');
      /* The dark pill under the swiper points at whatever is on screen. */
      var vt = panel.querySelector('[data-ena-nav-vt]');
      if (vt && shown) {
        var url = shown.getAttribute('data-ena-nav-url');
        vt.hidden = !url;
        if (url) vt.setAttribute('href', url);
      }
    }

    /* Arrows move the visible track by one card (card width + gap, measured,
       not assumed) and switch themselves off at either end. Re-evaluated after
       every track swap and on the track's own scroll, so they stay honest when
       a row has fewer cards than fit. */
    function visibleTrack(panel) {
      return Array.prototype.filter.call(panel.querySelectorAll('[data-ena-nav-track]'), function (t) {
        return !t.hidden;
      })[0] || null;
    }

    function updateArrows(panel) {
      var prev = panel.querySelector('[data-ena-nav-prev]');
      var next = panel.querySelector('[data-ena-nav-next]');
      if (!prev || !next) return;
      var t = visibleTrack(panel);
      if (!t) { prev.disabled = true; next.disabled = true; return; }
      var max = t.scrollWidth - t.clientWidth;
      prev.disabled = t.scrollLeft <= 1;
      next.disabled = max <= 1 || t.scrollLeft >= max - 1;
    }

    function stepTrack(panel, dir) {
      var t = visibleTrack(panel);
      if (!t) return;
      var card = t.firstElementChild;
      var cs = window.getComputedStyle(t);
      var gap = parseFloat(cs.columnGap || cs.gap) || 16;
      var step = card ? card.getBoundingClientRect().width + gap : 252;
      var max = t.scrollWidth - t.clientWidth;
      var target = t.scrollLeft + dir * step;
      /* Finish the run instead of leaving a few pixels for one more click:
         the last card peeks by design, so the remainder is often tiny. */
      if (dir > 0 && max - target < step / 2) target = max;
      if (dir < 0 && target < step / 2) target = 0;
      t.scrollTo({ left: target, behavior: 'smooth' });
    }

    function setTitle(panel, text) {
      var title = panel.querySelector('[data-ena-nav-title]');
      if (!title) return;
      if (title.dataset.enaDefault === undefined) title.dataset.enaDefault = title.textContent;
      title.textContent = text === null ? title.dataset.enaDefault : text;
    }

    function activateRow(panel, row) {
      panel.querySelectorAll('[data-ena-nav-item].is-active').forEach(function (a) {
        if (a !== row) a.classList.remove('is-active');
      });
      row.classList.add('is-active');
      panel.classList.add('has-active');
      var label = row.querySelector('.ena-nav__item-label');
      setTitle(panel, label ? label.textContent.trim() : null);
      showTrack(panel, row.getAttribute('data-ena-nav-item'));
      updateArrows(panel);
    }

    function resetRows(panel) {
      setTitle(panel, null);
      panel.querySelectorAll('[data-ena-nav-item].is-active').forEach(function (a) {
        a.classList.remove('is-active');
      });
      panel.classList.remove('has-active');
      showTrack(panel, 'default');
      updateArrows(panel);
    }

    function close() {
      if (openId === null) return;
      var p = panelFor(openId);
      var t = nav.querySelector('[data-ena-nav-trigger="' + openId + '"]');
      if (p) { p.hidden = true; resetRows(p); }
      if (t) t.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
      openId = null;
    }

    function open(id) {
      if (openId === id) return close();
      close();
      var p = panelFor(id);
      var t = nav.querySelector('[data-ena-nav-trigger="' + id + '"]');
      if (!p || !t) return;
      p.hidden = false;
      t.setAttribute('aria-expanded', 'true');
      nav.classList.add('is-open');
      openId = id;
      openedAtY = window.scrollY;
      nav.dispatchEvent(new CustomEvent('ena:panel-open'));
      /* "Conoce más" opens with its first pill already active, since the panel
         has no meaningful default state of its own. */
      if (p.getAttribute('data-ena-nav-autofirst') === '1') {
        var first = p.querySelector('[data-ena-nav-item]');
        if (first) activateRow(p, first);
      }
      /* Layout has to settle before scrollWidth is meaningful. */
      window.requestAnimationFrame(function () { updateArrows(p); });
    }

    var hoverMode = nav.getAttribute('data-ena-nav-hover') === '1' && window.matchMedia('(hover: hover)').matches;

    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        cancelTimers();
        var id = t.getAttribute('data-ena-nav-trigger');
        /* In hover mode the panel is usually already open by the time the
           click lands, so a toggle would close what the user just opened.
           There the click simply confirms; toggling is for click mode. */
        if (hoverMode && openId === id) return;
        open(id);
      });
    });

    /* HOVER TO OPEN (section setting, default on). Two small delays make it
       feel intentional rather than twitchy: the panel opens only after the
       cursor has rested on a menu entry for a beat, so sweeping across the bar
       does not flip panels; and it closes only after the cursor has been off
       both the entry and the panel for a beat, so crossing the gap between
       them does not slam it shut. Click still toggles, and is the only way in
       on touch screens, where hover does not exist. */
    var openTimer = null;
    var closeTimer = null;

    function cancelTimers() {
      if (openTimer) { clearTimeout(openTimer); openTimer = null; }
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    if (hoverMode) {
      triggers.forEach(function (t) {
        var id = t.getAttribute('data-ena-nav-trigger');
        t.addEventListener('mouseenter', function () {
          pointerInside = true;
          cancelTimers();
          if (openId === id) return;
          openTimer = setTimeout(function () {
            openTimer = null;
            if (openId !== id) { close(); open(id); }
          }, 120);
        });
        t.addEventListener('mouseleave', function () {
          pointerInside = false;
          if (openTimer) { clearTimeout(openTimer); openTimer = null; }
          scheduleClose();
        });
      });

      nav.querySelectorAll('[data-ena-nav-panel]').forEach(function (panel) {
        /* Bound to the white box, not the viewport-wide wrapper: leaving the
           box sideways must count as leaving the menu (22/09, Luchi). */
        var box = panel.querySelector('.ena-nav__panel-inner') || panel;
        box.addEventListener('mouseenter', function () { pointerInside = true; cancelTimers(); });
        box.addEventListener('mouseleave', function () { pointerInside = false; scheduleClose(); });
      });

      /* Plain links in the bar (Ofertas) close an open panel as the cursor
         reaches them, the way native menus do. */
      nav.querySelectorAll('.ena-nav__trigger--plain').forEach(function (a) {
        a.addEventListener('mouseenter', function () { cancelTimers(); close(); });
      });
    }

    function scheduleClose() {
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { closeTimer = null; close(); }, 180);
    }

    nav.querySelectorAll('[data-ena-nav-panel]').forEach(function (panel) {
      panel.querySelectorAll('[data-ena-nav-item]').forEach(function (row) {
        var on = function () { activateRow(panel, row); };
        row.addEventListener('mouseenter', on);
        row.addEventListener('focus', on);
      });

      var prev = panel.querySelector('[data-ena-nav-prev]');
      var next = panel.querySelector('[data-ena-nav-next]');
      if (prev) prev.addEventListener('click', function () { stepTrack(panel, -1); });
      if (next) next.addEventListener('click', function () { stepTrack(panel, 1); });
      panel.querySelectorAll('[data-ena-nav-track]').forEach(function (t) {
        t.addEventListener('scroll', function () { updateArrows(panel); }, { passive: true });
      });
    });

    document.addEventListener('click', function (e) {
      if (openId !== null && !nav.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
    /* A panel left open while the page scrolls away from it looks detached,
       floating or not. Measured from where it was opened rather than from the
       top, so a sticky non-floating bar that is already scrolled works too. */
    window.addEventListener('scroll', function () {
      if (openId !== null && !pointerInside && Math.abs(window.scrollY - openedAtY) > 80) close();
    }, { passive: true });
  }

  /* -------------------------------------------------------------------------
     4. SPACER. Measures the floating bar and gives the in-flow spacer next to it
        the same height plus the top and bottom margins, so page content starts
        under the bar instead of behind it. Re-measured on resize and after the
        announcement bar is adopted, which changes the bar's height.
     ------------------------------------------------------------------------- */
  /* The dropdown must fit under the bar: viewport minus the bar's bottom edge
     minus a 24px breathing gap (22/09). Set as a custom property the CSS reads. */
  function initPanelHeight(nav) {
    function fit() {
      var shell = nav.querySelector('.ena-nav__shell');
      if (!shell) return;
      var top = parseFloat(window.getComputedStyle(nav).top) || 48;
      var compact = nav.classList.contains('is-compact') || nav.classList.contains('ena-nav--static');
      var barBottom = (compact ? 0 : top) + shell.offsetHeight + 8;
      var max = Math.max(320, window.innerHeight - barBottom - 24);
      nav.style.setProperty('--ena-nav-panel-max', max + 'px');
    }
    fit();
    window.addEventListener('resize', fit, { passive: true });
    window.requestAnimationFrame(fit);
    window.setTimeout(fit, 300);
    nav.addEventListener('ena:panel-open', fit);
  }

  function initSpacer(nav) {
    var spacer = nav.nextElementSibling;
    if (!spacer || !spacer.hasAttribute('data-ena-nav-spacer')) return;

    function measure() {
      var shell = nav.querySelector('.ena-nav__shell');
      if (!shell) return;
      var top = parseFloat(window.getComputedStyle(nav).top) || 48;
      var h = shell.offsetHeight + top + 32;
      spacer.style.setProperty('--ena-nav-spacer', h + 'px');
    }

    measure();
    window.addEventListener('resize', measure, { passive: true });
    /* The announcement adoption runs in the same init pass; measure again once
       layout has settled so the spacer includes it. */
    window.requestAnimationFrame(measure);
    window.setTimeout(measure, 300);
  }

  function init(root) {
    var navs = (root || document).querySelectorAll('ena-mega-nav');
    navs.forEach(function (nav) {
      if (nav.dataset.enaNavReady === '1') return;
      nav.dataset.enaNavReady = '1';
      initCompact(nav);
      initAnnouncement(nav);
      initPanels(nav);
      initSpacer(nav);
      initPanelHeight(nav);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* The theme editor re-renders a section on every settings change, which drops
     the adopted bar back into a fresh DOM. Re-running puts it back in the slot
     so the merchant does not have to reload to see it. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    init(document);
  });
})();


/* ==========================================================================
   MOBILE MENU (22/09, Luchi): inside Impact's navigation drawer, every
   sub-list (categories under Productos, objectives, and the products under a
   category) is sorted alphabetically, and its "Ver todos" link is moved to
   the top and styled as a button. The top-level list (Productos, Según tu
   objetivo, ...) keeps the order set in Navigation. DOM-only: Impact's
   snippet is not edited. Runs once per list.
   ========================================================================== */
(function () {
  function isBack(li) {
    return !!li.querySelector('button[data-panel]') && /text-subdued|h6/.test(li.querySelector('button[data-panel]').className);
  }
  function label(li) {
    var a = li.querySelector('a, button');
    return (a ? a.textContent : li.textContent).replace(/\s+/g, ' ').trim();
  }
  function tidy(ul) {
    if (ul.dataset.enaTidy === '1') return;
    ul.dataset.enaTidy = '1';
    var items = Array.prototype.slice.call(ul.children).filter(function (el) { return el.tagName === 'LI'; });
    if (items.length < 2) return;
    var back = items.filter(isBack);
    /* The "see all" row is "Ver todos" inside a category, but at the category
       level the client names it "Todos los productos" / "Todas las ...". Both
       count, so every list gets its button at the top (22/09, Luchi). */
    var all = items.filter(function (li) { return !isBack(li) && /^(ver tod[oa]s|todos los|todas las)/i.test(label(li)); });
    var rest = items.filter(function (li) { return back.indexOf(li) === -1 && all.indexOf(li) === -1; });
    rest.sort(function (a, b) { return label(a).localeCompare(label(b), 'es', { sensitivity: 'base' }); });
    all.forEach(function (li) {
      var a = li.querySelector('a');
      if (a) a.classList.add('ena-drawer-all');
    });
    back.concat(all, rest).forEach(function (li) { ul.appendChild(li); });
  }
  function run(root) {
    /* Sub-panels only. Impact hoists them out of their list items at init, so
       nesting is not a usable test; what every sub-panel has and the root list
       never does is the "back" button as its first row. The root list
       (Productos, Según tu objetivo, ...) keeps the order set in Navigation. */
    (root || document).querySelectorAll('navigation-drawer .panel__wrapper ul.v-stack').forEach(function (ul) {
      var rows = Array.prototype.slice.call(ul.children).filter(function (el) { return el.tagName === 'LI'; });
      if (rows.some(isBack)) tidy(ul);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { run(document); });
  else run(document);
  document.addEventListener('shopify:section:load', function (e) { run(e.target); });
})();
